import React, { useState } from "react";
import moment from "moment";
import lodashGet from "lodash/get";
import { useSelector, useDispatch } from "react-redux";
// material ui
import withStyles from "@material-ui/styles/withStyles";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Divider from "@material-ui/core/Divider";
import Grid from "@material-ui/core/Grid";
import Typography from "@material-ui/core/Typography";
// charts
import ReactEchartsCore from "echarts-for-react/lib/core";
import echarts from "echarts/lib/echarts";
import "echarts/lib/chart/bar";
import "echarts/lib/component/legend";
import "echarts/lib/component/tooltip";
// icons
import ExcelIcon from "mdi-material-ui/FileExcel";
import ArrowBack from "@material-ui/icons/ArrowBack";
//jazasoft
import { PageFooter, Button, RestMethods, FETCH_START, FETCH_END } from "jazasoft";
//local
import Report from "./Report";
import CardHeader from "../../../components/CardHeader";

//helpers
import { humanizeNumber, isEmpty, toFixed, toFixedValue } from "../../../utils/helpers";
import { dataProvider } from "../../../App";
import handleError from "../../../utils/handleError";
import { colorLightGreen, colorTomatoRed } from "../../../utils/colors";
import { MacroReportId } from "../../../constants";
import { useDeepMemo } from "../../../hooks";
import MuiTable from "../../../components/MuiTable";

const columnsDetail = [
  { dataKey: "date", title: "Date", filterOptions: { filter: true } },
  {
    dataKey: "poRef",
    title: "PO Ref",
    linkPath: record => {
      window.open(`/reports/micro/reconciliation/${record.orderId}/view`, "_blank");
    }
  },
  { dataKey: "customer", title: "Customer", filterOptions: { filter: true } },
  { dataKey: "unitId", title: "Unit", filterOptions: { filter: true } },
  { dataKey: "product", title: "Product", filterOptions: { filter: true } },
  { dataKey: "style", title: "Style" },
  { dataKey: "color", title: "Color" },
  {
    dataKey: "part",
    title: "Part",
    render: (value, _, row) => `${value}${!isEmpty(row.placement) ? ` - ${row.placement}` : ""}`,
    filterOptions: { filter: true }
  },
  { dataKey: "lot", title: "Lot", render: (value, _, row) => `${row.lotType === "Bulk" ? "Lot" : row.lotType}${value}` },
  { dataKey: "cutQty", title: "Cut Qty" },
  { dataKey: "csv", title: "CSV", filterOptions: { filter: true } },
  { dataKey: "netIssued", title: "Net \nIssued" },
  { dataKey: "netEndBits", title: "Net \nEnd Bits" },
  { dataKey: "endBitsUsedInPanel", title: "End Bits \nUsed in Panel" },
  { dataKey: "grossEndBits", title: "Gross \nEnd Bits" },
  { dataKey: "grossEndBitsPercent", title: "Gross \nEnd Bits (%)" }
];

const getEchartOptions = aggregateEndBitsReport => {
  if (!aggregateEndBitsReport || Object.keys(aggregateEndBitsReport).length === 0) return {};

  return {
    color: [colorLightGreen.main, colorTomatoRed.main],
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow"
      }
    },
    legend: {
      x: "center",
      y: "bottom",
      data: ["endBitsUsedInPanel", "netEndBits"]
    },
    grid: {
      left: "5%",
      right: "4%",
      top: "5%",
      containLabel: true
    },
    xAxis: [
      {
        type: "category",
        data: Object.keys(aggregateEndBitsReport)
      }
    ],
    yAxis: [
      {
        type: "value",
        axisLabel: {
          formatter: "{value}%"
        }
      }
    ],
    series: [
      {
        name: "endBitsUsedInPanel",
        type: "bar",
        stack: "endBits",
        barWidth: 25,
        data: Object.keys(aggregateEndBitsReport).map(month => aggregateEndBitsReport[month].endBitsUsedInPanel)
      },

      {
        name: "netEndBits",
        type: "bar",
        stack: "endBits",
        barWidth: 25,
        data: Object.keys(aggregateEndBitsReport).map(month => aggregateEndBitsReport[month].netEndBits),
        label: {
          show: true,
          color: "black",
          fontSize: 10,
          position: "top",
          formatter: param => {
            const month = Object.keys(aggregateEndBitsReport)[param.dataIndex];
            const record = aggregateEndBitsReport[month] || {};
            return `${record.grossEndBits}%`;
          }
        }
      }
    ]
  };
};

const styles = {
  root: {
    margin: "1.5em 0"
  },
  displayCenter: {
    display: "flex",
    justifyContent: "center"
  }
};

const EndBitsReportView = props => {
  const { classes, filter = {}, history } = props;
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [month, setMonth] = useState(null);
  const i18nMessages = useSelector(state => state.i18n?.messages || {});

  const reportList = props.orderList.map(e => ({
    ...e,
    month: moment(e.orderCloseDate).format("MMM, YY")
  }));

  const onDownload = () => {
    const fileName = "END_BITS.xlsx";
    const orders = reportList.filter(o => {
      let result = true;
      if (filter.start && filter.end) {
        result = o.orderCloseDate >= moment(filter.start).startOf("day").valueOf() && o.orderCloseDate <= moment(filter.end).endOf("day").valueOf();
      }
      if (result && filter.customerList?.length > 0) {
        result = filter.customerList.map(({ name }) => name).includes(o.customer);
      }
      if (result && filter.season) {
        result = filter.season === o.season;
      }
      if (result && filter.line) {
        result = filter.line === o.line;
      }
      if (result && filter.productList?.length > 0) {
        result = filter.productList.map(({ name }) => name).includes(o.product);
      }
      if (result && filter.supplierList?.length > 0) {
        result = filter.supplierList.map(({ name }) => name).includes(o.supplier);
      }
      if (result && filter.fabricPatternList?.length > 0) {
        result = filter.fabricPatternList.map(({ name }) => name).includes(o.fabricPattern);
      }
      if (result && filter.fabricCategoryList?.length > 0) {
        result = filter.fabricCategoryList.map(({ name }) => name).includes(o.fabricCategory);
      }
      return result;
    });

    const columns = [
      { dataKey: "unitId", title: "Unit" },
      { dataKey: "orderCloseDate", title: "Close Date", type: "date" },
      { dataKey: "poRef", title: "ERP No" },
      { dataKey: "customer", title: "Buyer" },
      { dataKey: "season", title: "Season" },
      { dataKey: "product", title: "Product" },
      { dataKey: "style", title: "Style" },
      { dataKey: "styleNo", title: "Style No" },
      { dataKey: "color", title: "Style Color" },
      { dataKey: "part", title: "Part" },
      { dataKey: "placement", title: "Placement" },
      { dataKey: "lot", title: "Lot" },
      { dataKey: "orderQty", title: "Order Qty" },
      { dataKey: "cutQty", title: "Cut Qty" },
      { dataKey: "cutQtyPercent", title: "Cut Qty (%)", cellStyle: { dataFormat: "0.0%" } },
      { dataKey: "rejectedQty", title: "Rejected Qty" },
      { dataKey: "csv", title: "CSV" },
      { dataKey: "netIssued", title: "Net Issued Fabric" },
      { dataKey: "avgRollLength", title: "Average Roll Length" },
      { dataKey: "splitCount", title: "No of Splitting" },
      { dataKey: "netEndBits", title: "Net End Bits" },
      { dataKey: "endBitsUsedInPanel", title: "Used In Panel Replacement" },
      { dataKey: "grossEndBits", title: "Gross End Bits" },
      { dataKey: "grossEndBitsPercent", title: "Gross End Bits(%)", cellStyle: { dataFormat: "0.00%" } },
      { dataKey: "supplier", title: "Supplier" }
    ].map((c, idx) => ({ ...c, colNo: idx + 1, title: lodashGet(i18nMessages, `resources.report.arr.excelColumns.${c.dataKey}`) || c.title }));

    const rowData = orders
      .map(r => {
        const netIssued = r.issued - r.returned;
        const netEndBits = r.endBits;
        const grossEndBits = r.grossEndBits;
        const endBitsUsedInPanel = grossEndBits - netEndBits;
        const avgRollLength = Math.round(netIssued / (r.rollCount - r.splitCount));
        const cutQtyPercent = r.orderQty > 0 ? toFixed(r.cutQty / r.orderQty, 3) : 0.0;
        return {
          ...r,
          lot: `${r.lotType === "Bulk" ? "Lot" : r.lotType}${r.lot}`,
          netIssued,
          avgRollLength,
          netEndBits,
          endBitsUsedInPanel,
          grossEndBitsPercent: toFixed(grossEndBits / netIssued, 4),
          cutQtyPercent
        };
      })
      .map((row, idx) => ({ rowNo: idx + 1, cellList: columns.map(c => ({ dataKey: c.dataKey, value: row[c.dataKey] })) }));

    const options = {
      url: `export/excel`,
      method: "post",
      responseType: "arraybuffer",
      data: [
        {
          sheetNo: 0,
          sheetName: "END BITS REPORT",
          autoSizeColumn: false,
          excelData: { columnList: columns, rowList: rowData }
        }
      ],
      params: { filename: fileName }
    };

    dispatch({ type: FETCH_START });
    dataProvider(RestMethods.CUSTOM, undefined, options)
      .then(response => {
        if (response.status === 200) {
          const header = response.headers["content-disposition"];
          const filename = header ? header.match(/filename="(.+)"/)[1] : "END_BITS.xlsx";

          const blob = new Blob([response.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          });
          const link = document.createElement("a");
          link.href = window.URL.createObjectURL(blob);
          link.download = filename;
          link.click();
        }
        dispatch({ type: FETCH_END });
      })
      .catch(err => {
        handleError(err, dispatch);
        dispatch({ type: FETCH_END });
      });
  };

  const onBack = () => {
    if (page === 2) {
      setPage(1);
      setMonth(null);
    } else {
      history.goBack();
    }
  };

  const columnsSummary = useDeepMemo(() => {
    return [
      {
        dataKey: "month",
        title: "Month",
        linkPath: record => {
          setPage(2);
          setMonth(record.month);
        }
      },
      { dataKey: "poCount", title: "Cut \nOrders", align: "center" },
      { dataKey: "cutQty", title: "Cut Qty", render: value => humanizeNumber(value), align: "right" },
      { dataKey: "netIssued", title: "Net \nIssued", render: value => humanizeNumber(toFixedValue(value, 0)), align: "right" },
      { dataKey: "netEndBitsTotal", title: "Net \nEnd Bits", align: "right" },
      { dataKey: "endBitsUsedInPanelTotal", title: "End Bits\n Used in Panel", align: "right" },
      { dataKey: "grossEndBitsTotal", title: "Gross \nEnd Bits", align: "right" },
      { dataKey: "grossEndBits", title: "Gross \nEnd Bits (%)", render: value => `${value}%`, align: "right" }
    ];
  }, [setPage, setMonth]);

  const { rows, options } = useDeepMemo(() => {
    let rows = [],
      options = {};
    if (page === 1) {
      let months = [];
      let curr = moment(filter.end);
      while (curr.valueOf() >= moment(filter.start).startOf("month").valueOf()) {
        months.unshift(curr.format("MMM, YY"));
        curr = curr.subtract(1, "months");
      }

      let aggregateEndBitsReport = {}; // month -> {[dataKey]: value}, where dataKey = month, noOfPos, ...
      months.forEach(month => {
        const monthReportList = reportList.filter(e => e.month === month);
        const cutQty = monthReportList.reduce((acc, el) => acc + el.cutQty, 0);
        const netIssued = monthReportList.reduce((acc, el) => acc + (el.issued - el.returned), 0);

        const netEndBitsTotal = monthReportList.reduce((acc, el) => acc + el.endBits, 0);
        const grossEndBitsTotal = monthReportList.reduce((acc, el) => acc + el.grossEndBits, 0);
        const endBitsUsedInPanelTotal = grossEndBitsTotal - netEndBitsTotal;

        const netEndBits = netIssued > 0 ? (netEndBitsTotal * 100) / netIssued : 0;
        const endBitsUsedInPanel = netIssued > 0 ? (endBitsUsedInPanelTotal * 100) / netIssued : 0;
        const grossEndBits = netIssued > 0 ? (grossEndBitsTotal * 100) / netIssued : 0;

        aggregateEndBitsReport[month] = {
          month,
          poCount: monthReportList.length,
          cutQty,
          netIssued: toFixed(netIssued, 1),
          netEndBitsTotal: toFixed(netEndBitsTotal, 1),
          netEndBits: toFixed(netEndBits, 1),
          endBitsUsedInPanelTotal: toFixed(endBitsUsedInPanelTotal, 1),
          endBitsUsedInPanel: toFixed(endBitsUsedInPanel, 1),
          grossEndBitsTotal: toFixed(grossEndBitsTotal, 1),
          grossEndBits: toFixed(grossEndBits, 1)
        };
      });

      rows = [...months].reverse().map(month => aggregateEndBitsReport[month]);
      options = getEchartOptions(aggregateEndBitsReport);
    } else if (page === 2) {
      rows = reportList
        .filter(e => e.month === month)
        .sort((a, b) => b.orderCloseDate - a.orderCloseDate)
        .map(order => ({
          ...order,
          ...order,
          date: moment(order.orderCloseDate).format("ll"),
          csv: `${order.csv === true ? "Yes" : "No"}`,
          netIssued: toFixed(order.issued - order.returned, 2),
          netEndBits: toFixed(order.endBits, 2),
          endBitsUsedInPanel: toFixed(order.grossEndBits - order.endBits),
          grossEndBits: toFixed(order.endBits),
          grossEndBitsPercent: `${toFixed((order.grossEndBits * 100) / (order.allocated - order.returned), 2)}%`
        }));
    }
    return { rows, options };
  }, [page, filter, reportList, month]);

  if (reportList.length === 0) {
    return (
      <div style={{ marginTop: "3em" }}>
        <Typography className={classes.displayCenter} color="primary" variant="subtitle1">
          Data not available
        </Typography>
      </div>
    );
  }

  return (
    <div className={classes.root}>
      <div className={classes.content}>
        {page === 1 && (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Card>
                <CardHeader title="Monthly End Bits (%)" />
                <CardContent>
                  <ReactEchartsCore
                    echarts={echarts}
                    option={options}
                    style={{ height: "250px", width: "100%" }}
                    opts={{ renderer: "svg" }}
                    className="react_for_echarts"
                  />
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Card>
                <CardHeader title="Monthly End Bits" />
                <Divider />
                <CardContent style={{ padding: 0, height: 288, overflowY: "auto" }}>
                  <MuiTable columns={columnsSummary} rows={rows} i18nKey={`resources.report.endbit`} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {page === 2 && (
          <MuiTable
            title={`Monthly End Bits: ${month}`}
            columns={columnsDetail}
            rows={rows}
            i18nKey={`resources.report.endbit`}
            pageable
            searchable
            searchKeys={["poRef", "style"]}
          />
        )}
      </div>

      <PageFooter className={classes.displayCenter}>
        <Button label="Back" variant="contained" onClick={onBack}>
          <ArrowBack />
        </Button>
        <Button label="Download" variant="contained" style={{ color: "green", marginLeft: "1em" }} onClick={onDownload}>
          <ExcelIcon />
        </Button>
      </PageFooter>
    </div>
  );
};

const StyledEndBitsReport = withStyles(styles)(EndBitsReportView);

const EndBitsReport = props => (
  <Report title="End Bits Report" defaultMonths={6} macroReportId={MacroReportId.END_BITS} {...props}>
    <StyledEndBitsReport {...props} />
  </Report>
);

export default EndBitsReport;
